import React, { useState, useEffect, useCallback } from 'react'
import Box from '@mui/joy/Box/index.js'
import Typography from '@mui/joy/Typography/index.js'
import Button from '@mui/joy/Button/index.js'
import Card from '@mui/joy/Card/index.js'
import CardContent from '@mui/joy/CardContent/index.js'
import Table from '@mui/joy/Table/index.js'
import Chip from '@mui/joy/Chip/index.js'
import CircularProgress from '@mui/joy/CircularProgress/index.js'
import Modal from '@mui/joy/Modal/index.js'
import ModalDialog from '@mui/joy/ModalDialog/index.js'
import ModalClose from '@mui/joy/ModalClose/index.js'
import IconButton from '@mui/joy/IconButton/index.js'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Stack from '@mui/joy/Stack/index.js'
import Select from '@mui/joy/Select/index.js'
import Option from '@mui/joy/Option/index.js'
import FormControl from '@mui/joy/FormControl/index.js'
import FormLabel from '@mui/joy/FormLabel/index.js'
import Checkbox from '@mui/joy/Checkbox/index.js'
import FormHelperText from '@mui/joy/FormHelperText/index.js'
import Divider from '@mui/joy/Divider/index.js'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import PendingIcon from '@mui/icons-material/Pending'
import dayjs from 'dayjs'
import { get, post } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'
import {
  REVIEW_MODELS,
  ReviewModel,
  getModelName,
} from '../utils/review-models.js'

const DEFAULT_PROMPT = `Review these conversations and the retrieved citations and assign them a category. The categories should be Good/Didn't Answer/Needs Review. Needs Review should have sub labels like "Irrelevant Citations" "conflicting citations" "user rated unhelpful" "hallucination/false information suspected" and "other"`

export default function LLMRatingAnalysis() {
  const [reviewPasses, setReviewPasses] = useState([])
  const [selectedPass, setSelectedPass] = useState(null)
  const [conversationReviews, setConversationReviews] = useState([])
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0)
  const [conversationDetails, setConversationDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingReviews, setLoadingReviews] = useState(false)
  const [loadingConversation, setLoadingConversation] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectPassModalOpen, setSelectPassModalOpen] = useState(false)
  const [hasMoreReviews, setHasMoreReviews] = useState(false)
  const [currentFilterLabel, setCurrentFilterLabel] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [pageTokens, setPageTokens] = useState([null]) // Array of tokens for each page (page 0 starts with null)
  const [allReviews, setAllReviews] = useState([]) // All loaded reviews across pages

  // Form state for creating review pass
  const prompt = DEFAULT_PROMPT
  const [modelId, setModelId] = useState(ReviewModel.CLAUDE_HAIKU_4_5) // Default to fastest/cheapest
  const [filterByRating, setFilterByRating] = useState('all')
  const [baseReviewPassId, setBaseReviewPassId] = useState(null)
  const [isIncremental, setIsIncremental] = useState(false)

  const fetchReviewPasses = useCallback(
    async (showLoading = true) => {
      if (showLoading) {
        setLoading(true)
      }
      const s = await fetchAuthSession()
      const idToken = s.tokens?.idToken?.toString()

      try {
        const restOperation = get({
          apiName: 'technician-bot',
          path: '/1/admin/review/passes',
          options: {
            headers: {
              Authorization: idToken,
            },
          },
        })

        const { body } = await restOperation.response
        const response = await body.json()

        setReviewPasses(response.reviewPasses || [])

        // Update the selected pass if it's in the list
        if (selectedPass) {
          const updatedPass = response.reviewPasses?.find(
            (p) => p.reviewPassId === selectedPass.reviewPassId,
          )
          if (updatedPass) {
            setSelectedPass(updatedPass)
          }
        }
      } catch (error) {
        console.error('Error fetching review passes:', error)
      } finally {
        if (showLoading) {
          setLoading(false)
        }
      }
    },
    [selectedPass],
  )

  const createReviewPass = async () => {
    const s = await fetchAuthSession()
    const idToken = s.tokens?.idToken?.toString()

    const conversationFilter =
      filterByRating === 'all' ?
        {}
      : {
          userRating:
            filterByRating === 'positive' ? true
            : filterByRating === 'negative' ? false
            : null,
        }

    try {
      const restOperation = post({
        apiName: 'technician-bot',
        path: '/1/admin/review/passes',
        options: {
          headers: {
            Authorization: idToken,
          },
          body: {
            prompt,
            modelId,
            conversationFilter,
            baseReviewPassId: isIncremental ? baseReviewPassId : undefined,
          },
        },
      })

      await restOperation.response
      setCreateModalOpen(false)
      setIsIncremental(false)
      setBaseReviewPassId(null)
      void fetchReviewPasses() // Refresh the list
      // Polling will start automatically via the useEffect hook
    } catch (error) {
      console.error('Error creating review pass:', error)
    }
  }

  const resumeReviewPass = async (reviewPassId) => {
    const s = await fetchAuthSession()
    const idToken = s.tokens?.idToken?.toString()

    try {
      const restOperation = post({
        apiName: 'technician-bot',
        path: '/1/admin/review/passes/resume',
        options: {
          headers: {
            Authorization: idToken,
          },
          body: {
            reviewPassId,
          },
        },
      })

      await restOperation.response
      console.log(`Resumed review pass ${reviewPassId}`)
      void fetchReviewPasses() // Refresh the list
    } catch (error) {
      console.error('Error resuming review pass:', error)
    }
  }

  const retryFailedConversations = async (reviewPassId) => {
    const s = await fetchAuthSession()
    const idToken = s.tokens?.idToken?.toString()

    try {
      const restOperation = post({
        apiName: 'technician-bot',
        path: '/1/admin/review/passes/retry-failed',
        options: {
          headers: {
            Authorization: idToken,
          },
          body: {
            reviewPassId,
          },
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()
      console.log(
        `Retrying ${response.failedCount} failed conversations for review pass ${reviewPassId}`,
      )
      void fetchReviewPasses() // Refresh the list
    } catch (error) {
      console.error('Error retrying failed conversations:', error)
    }
  }

  const fetchConversationReviews = useCallback(
    async (reviewPassId, label = null, pageIndex = 0) => {
      setLoadingReviews(true)
      const s = await fetchAuthSession()
      const idToken = s.tokens?.idToken?.toString()

      const queryParams = new URLSearchParams({ reviewPassId })
      if (label) {
        queryParams.append('label', label)
      }

      // Use the token for the requested page
      const tokenForPage = pageTokens[pageIndex]
      if (tokenForPage) {
        queryParams.append('nextToken', tokenForPage)
      }

      try {
        const restOperation = get({
          apiName: 'technician-bot',
          path: `/1/admin/review/conversation-reviews?${queryParams}`,
          options: {
            headers: {
              Authorization: idToken,
            },
          },
        })

        const { body } = await restOperation.response
        const response = await body.json()

        const newReviews = response.reviews || []

        // Store reviews for this page
        setAllReviews((prev) => [
          ...prev.slice(0, pageIndex),
          newReviews,
          ...prev.slice(pageIndex + 1),
        ])

        // Update current page reviews
        setConversationReviews(newReviews)

        // Store the next page token if we haven't already
        if (response.nextToken && !pageTokens[pageIndex + 1]) {
          setPageTokens((prev) => [
            ...prev.slice(0, pageIndex + 1),
            response.nextToken,
            ...prev.slice(pageIndex + 2),
          ])
        }

        setHasMoreReviews(!!response.nextToken)
      } catch (error) {
        console.error('Error fetching conversation reviews:', error)
      } finally {
        setLoadingReviews(false)
      }
    },
    [pageTokens],
  )

  const fetchConversationDetails = async (conversationId, userId) => {
    setLoadingConversation(true)
    const s = await fetchAuthSession()
    const idToken = s.tokens?.idToken?.toString()

    try {
      const path = `/1/conversations/${conversationId}?username=${encodeURIComponent(userId)}`

      const restOperation = get({
        apiName: 'technician-bot',
        path,
        options: {
          headers: {
            Authorization: idToken,
          },
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      setConversationDetails(response)
    } catch (error) {
      console.error('Error fetching conversation details:', error)
      setConversationDetails(null)
    } finally {
      setLoadingConversation(false)
    }
  }

  const viewPassDetails = useCallback(
    async (pass) => {
      setSelectedPass(pass)
      setCurrentReviewIndex(0)
      setConversationDetails(null)
      setSelectPassModalOpen(false)
      setHasMoreReviews(false)
      setCurrentFilterLabel(null)
      setCurrentPage(0)
      setPageTokens([null])
      setAllReviews([])
      await fetchConversationReviews(pass.reviewPassId, null, 0)
    },
    [fetchConversationReviews],
  )

  const goToPage = useCallback(
    async (pageIndex) => {
      if (!selectedPass) return

      // Check if we already have this page cached
      if (allReviews[pageIndex]) {
        setCurrentPage(pageIndex)
        setConversationReviews(allReviews[pageIndex])
        setCurrentReviewIndex(0)
      } else {
        // Fetch the page
        setCurrentPage(pageIndex)
        setCurrentReviewIndex(0)
        await fetchConversationReviews(
          selectedPass.reviewPassId,
          currentFilterLabel,
          pageIndex,
        )
      }
    },
    [selectedPass, allReviews, fetchConversationReviews, currentFilterLabel],
  )

  const goToNextPage = useCallback(async () => {
    if (hasMoreReviews) {
      await goToPage(currentPage + 1)
    }
  }, [hasMoreReviews, currentPage, goToPage])

  const goToPreviousPage = useCallback(async () => {
    if (currentPage > 0) {
      await goToPage(currentPage - 1)
    }
  }, [currentPage, goToPage])

  const handleReviewNavigation = async (direction) => {
    const newIndex =
      direction === 'next' ? currentReviewIndex + 1 : currentReviewIndex - 1

    if (newIndex >= 0 && newIndex < conversationReviews.length) {
      setCurrentReviewIndex(newIndex)
      const review = conversationReviews[newIndex]
      await fetchConversationDetails(review.conversationId, review.userId)
    }
  }

  useEffect(() => {
    void fetchReviewPasses()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount
  }, [])

  // Auto-select most recent review pass when loaded
  useEffect(() => {
    if (reviewPasses.length > 0 && !selectedPass) {
      const mostRecent = reviewPasses[0]
      void viewPassDetails(mostRecent)
    }
  }, [reviewPasses, selectedPass, viewPassDetails])

  // Load conversation details when reviews are fetched or index changes
  useEffect(() => {
    if (
      conversationReviews.length > 0 &&
      currentReviewIndex < conversationReviews.length
    ) {
      const review = conversationReviews[currentReviewIndex]
      void fetchConversationDetails(review.conversationId, review.userId)
    }
  }, [conversationReviews, currentReviewIndex])

  // Polling effect - polls when there are running passes
  useEffect(() => {
    // Check if there's any pass that's running
    const hasRunningPass = reviewPasses.some(
      (pass) => pass.status === 'running',
    )

    if (hasRunningPass) {
      console.log('Starting polling - running pass detected')
      // Start polling
      const intervalId = setInterval(() => {
        console.log('Polling for review pass updates...')
        void fetchReviewPasses(false) // Poll without showing loading spinner
      }, 5000) // Poll every 5 seconds

      // Cleanup function to stop polling
      return () => {
        console.log('Stopping polling')
        clearInterval(intervalId)
      }
    } else {
      console.log('No running passes - polling not active')
    }
  }, [reviewPasses, fetchReviewPasses])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />
      case 'running':
        return <CircularProgress size="sm" />
      case 'failed':
        return <ErrorIcon color="danger" />
      default:
        return <PendingIcon />
    }
  }

  const getLabelColor = (label) => {
    switch (label) {
      case 'Good':
        return 'success'
      case "Didn't Answer":
        return 'warning'
      case 'Needs Review':
        return 'danger'
      default:
        return 'neutral'
    }
  }

  const currentReview =
    conversationReviews.length > 0 ?
      conversationReviews[currentReviewIndex]
    : null

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (reviewPasses.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Typography level="body-md" sx={{ mb: 2 }}>
              No review passes yet. Click &quot;Start New Review Pass&quot; to
              analyze conversations with an LLM.
            </Typography>
            <Button
              startDecorator={<PlayArrowIcon />}
              onClick={() => {
                setCreateModalOpen(true)
              }}
            >
              Start New Review Pass
            </Button>
          </CardContent>
        </Card>

        {/* Create Review Pass Modal */}
        <Modal
          open={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false)
          }}
        >
          <ModalDialog sx={{ minWidth: 600 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Start New Review Pass
            </Typography>

            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Model</FormLabel>
                <Select
                  value={modelId}
                  onChange={(_, value) => {
                    setModelId(value)
                  }}
                >
                  {REVIEW_MODELS.map((model) => (
                    <Option key={model.id} value={model.id}>
                      {model.name} ({model.costDisplay})
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <Checkbox
                  label="Incremental review (only review new conversations)"
                  checked={isIncremental}
                  onChange={(e) => {
                    setIsIncremental(e.target.checked)
                  }}
                />
                <FormHelperText>
                  If enabled, only conversations not reviewed in the selected
                  base pass will be reviewed
                </FormHelperText>
              </FormControl>

              {isIncremental && (
                <FormControl>
                  <FormLabel>Base Review Pass</FormLabel>
                  <Select
                    value={baseReviewPassId}
                    onChange={(_, value) => {
                      setBaseReviewPassId(value)
                    }}
                    placeholder="Select a base review pass..."
                  >
                    {reviewPasses
                      .filter((pass) => pass.status === 'completed')
                      .map((pass) => (
                        <Option
                          key={pass.reviewPassId}
                          value={pass.reviewPassId}
                        >
                          {dayjs(pass.createdAt).format('MMM D, YYYY h:mm A')} -{' '}
                          {pass.conversationsProcessed} conversations
                        </Option>
                      ))}
                  </Select>
                  <FormHelperText>
                    Select a completed review pass to use as the base
                  </FormHelperText>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Filter Conversations</FormLabel>
                <Select
                  value={filterByRating}
                  onChange={(_, value) => {
                    setFilterByRating(value)
                  }}
                >
                  <Option value="all">All Conversations</Option>
                  <Option value="positive">Only Thumbs Up</Option>
                  <Option value="negative">Only Thumbs Down</Option>
                  <Option value="none">Only Unrated</Option>
                </Select>
              </FormControl>

              <Button
                onClick={() => {
                  void createReviewPass()
                }}
                startDecorator={<PlayArrowIcon />}
                disabled={isIncremental && !baseReviewPassId}
              >
                Start Review Pass
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with current review pass info */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Typography level="h4">LLM Rating Analysis</Typography>
          {selectedPass && (
            <>
              <Divider orientation="vertical" />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {getStatusIcon(selectedPass.status)}
                <Box>
                  <Typography level="body-sm" fontWeight="bold">
                    {dayjs(selectedPass.createdAt).format('MMM D, YYYY h:mm A')}
                    {selectedPass.isIncremental && (
                      <Chip size="sm" color="primary" sx={{ ml: 1 }}>
                        Incremental
                      </Chip>
                    )}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {getModelName(selectedPass.modelId)} •{' '}
                    {selectedPass.conversationsProcessed || 0} /{' '}
                    {selectedPass.totalConversationsToProcess} conversations
                    {selectedPass.conversationsFailed > 0 && (
                      <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        {' '}
                        ({selectedPass.conversationsFailed} failed)
                      </span>
                    )}
                    {' • $'}
                    {selectedPass.estimatedCostUSD?.toFixed(4) || '0.0000'}
                    {selectedPass.createdBy &&
                      ` • by ${selectedPass.createdBy}`}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectPassModalOpen(true)
            }}
          >
            Select Review Pass
          </Button>
          <Button
            startDecorator={<PlayArrowIcon />}
            onClick={() => {
              setCreateModalOpen(true)
            }}
          >
            Start New Review Pass
          </Button>
        </Stack>
      </Box>

      {/* Main content area - Review details */}
      {selectedPass ?
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left sidebar - Labels and filters */}
          <Box
            sx={{
              width: '280px',
              borderRight: '1px solid',
              borderColor: 'divider',
              p: 2,
              overflowY: 'auto',
            }}
          >
            <Typography
              level="body-xs"
              sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 'bold' }}
            >
              Filter by Label
            </Typography>
            <Stack spacing={1}>
              <Card
                variant="soft"
                color="success"
                size="sm"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setCurrentReviewIndex(0)
                  setCurrentPage(0)
                  setPageTokens([null])
                  setAllReviews([])
                  setCurrentFilterLabel('Good')
                  void fetchConversationReviews(
                    selectedPass.reviewPassId,
                    'Good',
                    0,
                  )
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Typography level="body-sm" fontWeight="bold">
                    Good: {selectedPass.labelCounts?.Good || 0}
                  </Typography>
                </CardContent>
              </Card>

              <Card
                variant="soft"
                color="warning"
                size="sm"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setCurrentReviewIndex(0)
                  setCurrentPage(0)
                  setPageTokens([null])
                  setAllReviews([])
                  setCurrentFilterLabel("Didn't Answer")
                  void fetchConversationReviews(
                    selectedPass.reviewPassId,
                    "Didn't Answer",
                    0,
                  )
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Typography level="body-sm" fontWeight="bold">
                    Didn&apos;t Answer:{' '}
                    {selectedPass.labelCounts?.["Didn't Answer"] || 0}
                  </Typography>
                </CardContent>
              </Card>

              <Card
                variant="soft"
                color="danger"
                size="sm"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setCurrentReviewIndex(0)
                  setCurrentPage(0)
                  setPageTokens([null])
                  setAllReviews([])
                  setCurrentFilterLabel('Needs Review')
                  void fetchConversationReviews(
                    selectedPass.reviewPassId,
                    'Needs Review',
                    0,
                  )
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Typography level="body-sm" fontWeight="bold">
                    Needs Review:{' '}
                    {selectedPass.labelCounts?.['Needs Review'] || 0}
                  </Typography>
                </CardContent>
              </Card>

              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  setCurrentReviewIndex(0)
                  setCurrentPage(0)
                  setPageTokens([null])
                  setAllReviews([])
                  setCurrentFilterLabel(null)
                  void fetchConversationReviews(
                    selectedPass.reviewPassId,
                    null,
                    0,
                  )
                }}
              >
                Show All
              </Button>
            </Stack>
          </Box>

          {/* Main review content */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {loadingReviews ?
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            : conversationReviews.length > 0 && currentReview ?
              <>
                {/* Navigation header */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography level="title-md">Review Details</Typography>
                    <Chip size="sm" color={getLabelColor(currentReview.label)}>
                      {currentReview.label}
                    </Chip>
                    {currentReview.subLabels &&
                      currentReview.subLabels.length > 0 && (
                        <Stack direction="row" spacing={0.5}>
                          {currentReview.subLabels.map((subLabel) => (
                            <Chip key={subLabel} size="sm" variant="outlined">
                              {subLabel}
                            </Chip>
                          ))}
                        </Stack>
                      )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        level="body-sm"
                        sx={{ color: 'text.secondary' }}
                      >
                        Page {currentPage + 1} • Item{' '}
                        {currentPage * 50 + currentReviewIndex + 1}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="sm"
                          variant="outlined"
                          disabled={currentPage === 0}
                          onClick={() => {
                            void goToPreviousPage()
                          }}
                        >
                          Previous Page
                        </Button>
                        <Button
                          size="sm"
                          variant="outlined"
                          disabled={!hasMoreReviews}
                          onClick={() => {
                            void goToNextPage()
                          }}
                        >
                          Next Page
                        </Button>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="sm"
                        disabled={currentReviewIndex === 0}
                        onClick={() => {
                          void handleReviewNavigation('prev')
                        }}
                      >
                        <ArrowBackIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        disabled={
                          currentReviewIndex === conversationReviews.length - 1
                        }
                        onClick={() => {
                          void handleReviewNavigation('next')
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>

                {/* Review content */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography
                        level="body-xs"
                        sx={{ color: 'text.secondary', mb: 0.5 }}
                      >
                        Conversation ID
                      </Typography>
                      <Typography
                        level="body-sm"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {currentReview.conversationId}
                      </Typography>
                    </Box>

                    {currentReview.reasoning && (
                      <Box>
                        <Typography
                          level="body-xs"
                          sx={{ color: 'text.secondary', mb: 0.5 }}
                        >
                          Reasoning
                        </Typography>
                        <Card variant="soft">
                          <CardContent>
                            <Typography level="body-sm">
                              {currentReview.reasoning}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                    )}

                    <Divider />

                    {/* Conversation Display */}
                    {loadingConversation ?
                      <Box sx={{ textAlign: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    : conversationDetails ?
                      <Box>
                        <Typography level="title-sm" sx={{ mb: 2 }}>
                          Conversation
                        </Typography>
                        {conversationDetails.messages?.map((message, index) => (
                          <Box
                            key={message.messageId || index}
                            sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: 'md',
                              backgroundColor:
                                message.author === 'bot' ?
                                  'background.level1'
                                : 'primary.softBg',
                            }}
                          >
                            <Typography
                              level="body-xs"
                              fontWeight="bold"
                              sx={{ mb: 1 }}
                            >
                              {message.author === 'bot' ? 'Assistant' : 'User'}
                            </Typography>
                            <Typography
                              level="body-md"
                              sx={{ whiteSpace: 'pre-wrap' }}
                            >
                              {message.message}
                            </Typography>
                            {message.context && message.context.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  level="body-xs"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  Citations (
                                  {message.context.reduce(
                                    (acc, ctx) =>
                                      acc +
                                      (ctx.retrievedReferences?.length || 0),
                                    0,
                                  )}
                                  ):
                                </Typography>
                                <Stack spacing={1}>
                                  {message.context.map((ctx, ctxIndex) =>
                                    ctx.retrievedReferences?.map(
                                      (ref, refIndex) => (
                                        <Card
                                          key={`${ctxIndex}-${refIndex}`}
                                          variant="outlined"
                                          size="sm"
                                        >
                                          <CardContent sx={{ p: 1 }}>
                                            <Typography
                                              level="body-xs"
                                              fontWeight="bold"
                                            >
                                              {ref.metadata?.CacheTitle ||
                                                'Reference'}
                                            </Typography>
                                            {ref.metadata?.LitOrderNbr && (
                                              <Typography
                                                level="body-xs"
                                                sx={{ color: 'text.tertiary' }}
                                              >
                                                {ref.metadata.LitOrderNbr}
                                              </Typography>
                                            )}
                                          </CardContent>
                                        </Card>
                                      ),
                                    ),
                                  )}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    : <Typography
                        level="body-sm"
                        sx={{ color: 'text.secondary' }}
                      >
                        No conversation details available
                      </Typography>
                    }
                  </Stack>
                </Box>
              </>
            : <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                  No reviews found
                </Typography>
              </Box>
            }
          </Box>
        </Box>
      : <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            Select a review pass to view details
          </Typography>
        </Box>
      }

      {/* Select Review Pass Modal */}
      <Modal
        open={selectPassModalOpen}
        onClose={() => {
          setSelectPassModalOpen(false)
        }}
      >
        <ModalDialog sx={{ minWidth: 1200, maxWidth: '95vw', width: '90vw' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Select Review Pass
          </Typography>

          <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Table sx={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>Status</th>
                  <th style={{ width: '160px' }}>Created</th>
                  <th style={{ width: '140px' }}>Model</th>
                  <th style={{ width: '180px' }}>Creator</th>
                  <th style={{ width: '120px' }}>Conversations</th>
                  <th style={{ width: '200px' }}>Labels</th>
                  <th style={{ width: '140px' }}>Cost</th>
                  <th style={{ width: '220px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviewPasses.map((pass) => (
                  <tr key={pass.reviewPassId}>
                    <td>{getStatusIcon(pass.status)}</td>
                    <td>
                      <Typography level="body-sm">
                        {dayjs(pass.createdAt).format('MMM D, YYYY h:mm A')}
                      </Typography>
                      {pass.isIncremental && (
                        <Chip size="sm" color="primary" sx={{ mt: 0.5 }}>
                          Incremental
                        </Chip>
                      )}
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {getModelName(pass.modelId)}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {pass.createdBy || '-'}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {pass.conversationsProcessed || 0} /{' '}
                        {pass.totalConversationsToProcess}
                      </Typography>
                      {pass.conversationsFailed > 0 && (
                        <Typography
                          level="body-xs"
                          sx={{ color: 'danger.500', fontWeight: 'bold' }}
                        >
                          ({pass.conversationsFailed} failed)
                        </Typography>
                      )}
                    </td>
                    <td>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: 'wrap', gap: 0.5 }}
                      >
                        {pass.labelCounts?.Good > 0 && (
                          <Chip color="success" size="sm">
                            Good: {pass.labelCounts.Good}
                          </Chip>
                        )}
                        {pass.labelCounts?.["Didn't Answer"] > 0 && (
                          <Chip color="warning" size="sm">
                            No Answer: {pass.labelCounts["Didn't Answer"]}
                          </Chip>
                        )}
                        {pass.labelCounts?.['Needs Review'] > 0 && (
                          <Chip color="danger" size="sm">
                            Review: {pass.labelCounts['Needs Review']}
                          </Chip>
                        )}
                      </Stack>
                    </td>
                    <td>
                      <Typography level="body-sm" fontWeight="bold">
                        ${pass.estimatedCostUSD?.toFixed(4) || '0.0000'}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{ color: 'text.secondary' }}
                      >
                        {pass.totalInputTokens?.toLocaleString() || 0} in /{' '}
                        {pass.totalOutputTokens?.toLocaleString() || 0} out
                      </Typography>
                    </td>
                    <td>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="sm"
                          variant={
                            selectedPass?.reviewPassId === pass.reviewPassId ?
                              'solid'
                            : 'outlined'
                          }
                          onClick={() => {
                            void viewPassDetails(pass)
                          }}
                        >
                          {selectedPass?.reviewPassId === pass.reviewPassId ?
                            'Selected'
                          : 'Select'}
                        </Button>
                        {(pass.status === 'failed' ||
                          (pass.status === 'completed' &&
                            pass.conversationsProcessed <
                              pass.totalConversationsToProcess)) && (
                          <Button
                            size="sm"
                            color="warning"
                            onClick={() => {
                              void resumeReviewPass(pass.reviewPassId)
                            }}
                          >
                            Resume
                          </Button>
                        )}
                        {pass.conversationsFailed > 0 && (
                          <Button
                            size="sm"
                            color="danger"
                            onClick={() => {
                              void retryFailedConversations(pass.reviewPassId)
                            }}
                          >
                            Retry Failed
                          </Button>
                        )}
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Create Review Pass Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false)
        }}
      >
        <ModalDialog sx={{ minWidth: 600 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Start New Review Pass
          </Typography>

          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Model</FormLabel>
              <Select
                value={modelId}
                onChange={(_, value) => {
                  setModelId(value)
                }}
              >
                {REVIEW_MODELS.map((model) => (
                  <Option key={model.id} value={model.id}>
                    {model.name} ({model.costDisplay})
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <Checkbox
                label="Incremental review (only review new conversations)"
                checked={isIncremental}
                onChange={(e) => {
                  setIsIncremental(e.target.checked)
                }}
              />
              <FormHelperText>
                If enabled, only conversations not reviewed in the selected base
                pass will be reviewed
              </FormHelperText>
            </FormControl>

            {isIncremental && (
              <FormControl>
                <FormLabel>Base Review Pass</FormLabel>
                <Select
                  value={baseReviewPassId}
                  onChange={(_, value) => {
                    setBaseReviewPassId(value)
                  }}
                  placeholder="Select a base review pass..."
                >
                  {reviewPasses
                    .filter((pass) => pass.status === 'completed')
                    .map((pass) => (
                      <Option key={pass.reviewPassId} value={pass.reviewPassId}>
                        {dayjs(pass.createdAt).format('MMM D, YYYY h:mm A')} -{' '}
                        {pass.conversationsProcessed} conversations
                      </Option>
                    ))}
                </Select>
                <FormHelperText>
                  Select a completed review pass to use as the base
                </FormHelperText>
              </FormControl>
            )}

            <FormControl>
              <FormLabel>Filter Conversations</FormLabel>
              <Select
                value={filterByRating}
                onChange={(_, value) => {
                  setFilterByRating(value)
                }}
              >
                <Option value="all">All Conversations</Option>
                <Option value="positive">Only Thumbs Up</Option>
                <Option value="negative">Only Thumbs Down</Option>
                <Option value="none">Only Unrated</Option>
              </Select>
            </FormControl>

            <Button
              onClick={() => {
                void createReviewPass()
              }}
              startDecorator={<PlayArrowIcon />}
              disabled={isIncremental && !baseReviewPassId}
            >
              Start Review Pass
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  )
}
